import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLocalized } from "@/hooks/useLocalized";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import axios from "axios";
import { API } from "@/App";
import logger from "@/utils/logger";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import PackageCards from "./menu/PackageCards";
import { FeaturedDishes, AllDishes } from "./menu/MenuSections";

const MenuPage = () => {
  const { t } = useTranslation();
  const { l, lArr } = useLocalized();
  const [packages, setPackages] = useState([]);
  const [menuItems, setMenuItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("all");
  const featuredRef = useScrollReveal();
  const allDishesRef = useScrollReveal();
  const ctaRef = useScrollReveal();

  const fetchData = useCallback(async () => {
    try {
      const [pkgRes, menuRes] = await Promise.all([
        axios.get(`${API}/packages`),
        axios.get(`${API}/menu-items`),
      ]);
      setPackages(pkgRes.data);
      setMenuItems(menuRes.data);
    } catch (error) {
      logger.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const packageTypes = [
    { value: "all", label: t("menu.tabs.all") },
    { value: "wedding", label: t("menu.tabs.wedding") },
    { value: "corporate", label: t("menu.tabs.corporate") },
    { value: "traditional", label: t("menu.tabs.traditional") },
    { value: "private", label: t("menu.tabs.private") },
  ];

  const featuredItems = menuItems.filter((item) => item.is_featured);

  return (
    <div data-testid="menu-page" className="min-h-screen bg-[#F4F1DE]">
      {/* Hero */}
      <section className="pt-32 pb-20 bg-[#3D405B] relative overflow-hidden" data-testid="menu-hero">
        <div className="absolute inset-0 opacity-10 pattern-bg"></div>
        <div className="max-w-7xl mx-auto px-6 text-center relative z-10">
          <span className="inline-block bg-[#F2CC8F]/20 text-[#F2CC8F] px-4 py-2 rounded-full text-sm font-medium mb-6">
            {t("menu.hero.badge")}
          </span>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 font-['Playfair_Display']">
            {t("menu.hero.title")}
          </h1>
          <p className="text-xl text-[#F4F1DE]/80 max-w-2xl mx-auto">
            {t("menu.hero.subtitle")}
          </p>
        </div>
      </section>

      {/* Packages */}
      <section className="py-20" data-testid="packages-section">
        <div className="max-w-7xl mx-auto px-6">
          <Tabs defaultValue="all" value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex justify-center mb-12">
              <TabsList className="bg-white/50 p-1 rounded-full" data-testid="package-tabs">
                {packageTypes.map((type) => (
                  <TabsTrigger
                    key={type.value}
                    value={type.value}
                    data-testid={`tab-${type.value}`}
                    className="px-6 py-2 rounded-full data-[state=active]:bg-[#E07A5F] data-[state=active]:text-white"
                  >
                    {type.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
            <TabsContent value={activeTab} className="mt-0">
              <PackageCards packages={packages} loading={loading} activeTab={activeTab} l={l} lArr={lArr} />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      <FeaturedDishes items={featuredItems} loading={loading} sectionRef={featuredRef} l={l} />
      <AllDishes items={menuItems} loading={loading} sectionRef={allDishesRef} l={l} />

      {/* CTA */}
      <section className="py-20 bg-[#2D6A4F]" data-testid="menu-cta" ref={ctaRef}>
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="reveal">
            <img src="/logo.svg" alt="Djohomi Foods" className="w-20 h-20 object-contain mx-auto mb-6" />
          </div>
          <h2 className="reveal stagger-1 text-4xl font-bold text-white mb-4 font-['Playfair_Display']">
            {t("menu.cta.title")}
          </h2>
          <p className="reveal stagger-2 text-[#F4F1DE]/80 text-lg mb-8 max-w-xl mx-auto">
            {t("menu.cta.subtitle")}
          </p>
          <div className="reveal stagger-3">
            <Link to="/booking">
              <Button
                className="bg-[#F2CC8F] text-[#3D405B] hover:bg-[#e6c17d] text-lg px-10 py-6 rounded-full font-medium btn-hover-pulse"
                data-testid="menu-cta-btn"
              >
                {t("menu.cta.btn")}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MenuPage;
